import { Component, OnInit } from "@angular/core";
import { DialogService, DynamicDialogRef } from "primeng/dynamicdialog";
import { first } from "rxjs";
import { CxoneCustomDataImportDialogComponent } from "./components/cxone-custom-data-import-dialog/cxone-custom-data-import-dialog.component";
import { ColumnApi, FilterChangedEvent, GridApi, GridOptions, GridReadyEvent, RowDataUpdatedEvent, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import { ImportActionCellComponent } from './import-action-cell/import-action-cell.component';
import { ToastrManager } from '@niceltd/cxone-components/toastr';
import { CxoneEtlDataImportService } from "./cxone-etl-data-imports.service";
import { CVLocalizationService } from "src/app/reusables/services/cv-localization.service";
import { CustomerImportList, ETLDataImpotObj, PERMISSION, RawDataDefination } from "./cxone-etl-data-imports-model";
import moment from "moment";
import { CxoneHighlightSearchCellRendererComponent } from 'src/app/reusables/ag-grid-components/cxone-highlight-search-cell-renderer/cxone-highlight-search-cell-renderer.component';
import { AgGridHelperService } from 'src/app/reusables/services/ag-grid-helper.service';
import { CustomNoRowsOverlayComponent } from "../shared/custom-no-rows-overlay/custom-no-rows-overlay.component";
import { MessageModalComponent } from "@niceltd/cxone-components/modal";

@Component({
    selector: "cv-cxone-custom-data-import",
    templateUrl: "./cxone-custom-data-import.component.html",
    styleUrls: ["./cxone-custom-data-import.component.scss"],
    providers: [DialogService],
})
export class CxoneCustomDataImportComponent implements OnInit {
    public showViewDetailsPage = false;
    public importViewData: ETLDataImpotObj;
    public modalRef: DynamicDialogRef = null;
    public dataImportLabel: string;
    public dataImportGridData: ETLDataImpotObj[] = [];
    public rawDataDefinitions: RawDataDefination[] = [];
    public filteredDataImportGridData: ETLDataImpotObj[] = [];
    public searchInput = "";
    public searchPlaceholderText = "";
    public dataImportGridOptions: GridOptions;
    public gridApi: GridApi;
    public gridColumnApi: ColumnApi;
    public gridReady: Promise<boolean>;
    public showSpinner = false;
    public permissions: string[] = [];
    public showImportBtn = true;
    public showDeleteBtn = true;
    public showViewBtn = true;

    constructor(
        private dialogSrvc: DialogService,
        private cxoneEtlDataImportService: CxoneEtlDataImportService,
        private toasterManager: ToastrManager,
        private cvLocalizationService: CVLocalizationService,
        private agGridHelperService: AgGridHelperService,
    ) { }

    async ngOnInit(): Promise<void> {
        await this.loadLocalization();
        this.setGridOptions();
        this.permissions = await this.cxoneEtlDataImportService.getPermissions("CXOneCustomDataImport") || [];
        this.toggleElementPermissions();
    }

    toggleElementPermissions() {
        if (this.permissions.includes(PERMISSION.VIEW) && !this.permissions.includes(PERMISSION.CREATE)) {
            this.showImportBtn = false;
            this.showDeleteBtn = false;
        } else if (this.permissions.includes(PERMISSION.CREATE)) {
            this.showViewBtn = true;
            this.showImportBtn = true;
            this.showDeleteBtn = true;
        } else if (!this.permissions.includes(PERMISSION.VIEW) && !this.permissions.includes(PERMISSION.CREATE)) {
            this.showViewBtn = false;
            this.showImportBtn = false;
            this.showDeleteBtn = false;
        }
    }

    async loadDataImportList() {
        this.showSpinner = true;
        try {
            const response: CustomerImportList = await this.cxoneEtlDataImportService.customDataImport();
            this.dataImportGridData = response.ImportHistory;
            this.rawDataDefinitions = response.RawDataDefinitions;
            this.filteredDataImportGridData = [...this.dataImportGridData];
        }
        catch (error) {
            this.toasterManager
                .showToaster('error',
                    this.getLocalizationString('Internal_Server_Error', 'Internal server error. Try again.'));
        }
        finally {
            this.gridApi.setRowData(this.filteredDataImportGridData);
            this.showSpinner = false;
        }
    }

    setGridOptions() {
        this.dataImportGridOptions = {
            columnDefs: [
                {
                    field: 'FeedName',
                    headerName: this.getLocalizationString("Feed_Name", 'FEED NAME'),
                    cellRenderer: 'cxoneHighlightSearchRenderer',
                    rowGroup: true,
                    hide: true,
                    resizable: true,
                    sortable: true,
                    suppressSizeToFit: true,
                    suppressMovable: true
                },
                {
                    field: 'NumberOfRecords',
                    headerName: this.getLocalizationString("Number_Of_Records", 'NUMBER OF RECORDS'),
                    cellRenderer: 'cxoneHighlightSearchRenderer',
                    resizable: true,
                    sortable: true,
                    suppressSizeToFit: true,
                    minWidth: 315,
                    valueFormatter: (params: ValueFormatterParams) => params.data?.NumberOfRecords?.toString(),
                    suppressMovable: true
                },
                {
                    field: 'DateAdded',
                    headerName: this.getLocalizationString('IMPORTED_ON', 'IMPORTED ON'),
                    cellRenderer: 'cxoneHighlightSearchRenderer',
                    resizable: true,
                    sortable: true,
                    suppressSizeToFit: true,
                    valueGetter: (params: ValueGetterParams) => {
                        if (params.data == null) {
                            return null;
                        } else {
                            return params.data?.DateAdded ?
                                moment(params.data?.DateAdded).format('MMM D, YYYY h:mm:ss A') : 'NA';
                        }
                    },
                    minWidth: 315,
                    suppressMovable: true
                },
                {
                    colId: 'contextMenu',
                    type: 'simpleContextMenu',
                    cellClass: 'd-flex justify-content-center',
                    resizable: false,
                    sortable: false,
                    suppressSizeToFit: true,
                    width: 36,
                    suppressMovable: true
                }
            ],
            columnTypes: {
                simpleContextMenu: {
                    cellRendererSelector: (param) => {
                        if (param.data == null) {
                            return null;
                        }
                        else {
                            return { component: 'contextMenuCellRenderer', param };
                        }
                    }
                }
            },
            onGridReady: (event: GridReadyEvent) => this.onGridReady(event),
            onRowDataUpdated: (event: RowDataUpdatedEvent) => this.setRowCountLabel(),
            onFilterChanged: (event: FilterChangedEvent) => {
                this.gridApi.redrawRows();
                this.setRowCountLabel();
            },
            autoGroupColumnDef: {
                headerName: 'FEED NAME',
                headerClass: "group-header-align",
                cellRendererParams: {
                    suppressCount: true,
                    innerRenderer: 'cxoneHighlightSearchRenderer'
                }
            },
            groupDefaultExpanded: 1,
            components: {
                CustomNoRowsOverlayComponent,
                contextMenuCellRenderer: ImportActionCellComponent,
                cxoneHighlightSearchRenderer: CxoneHighlightSearchCellRendererComponent
            },
            noRowsOverlayComponent: 'CustomNoRowsOverlayComponent',
            noRowsOverlayComponentParams: {
                noRowsOverlayTextFunc: () => this.searchInput !== '' ?
                    this.getLocalizationString("No_Result_Found", "No result found")
                    : this.getLocalizationString("No_Data_Imported", "No data imported yet"),
                noRowsOverlayIconPathFunc: () => {
                    const path = this.searchInput !== '' ?
                        '../App_Themes/CXone/icons/no-results.svg#no-results'
                        : '../App_Themes/CXone/icons/empty-state.svg#empty-state';
                    return path;
                }
            },
            context: this,
            icons: {
                groupContracted:
                    `<div class="sortIcons" id="icon-right" style="margin-bottom: 0; align-content: flex-end">
                        <svg class="icon-10" id="sort-right-svg">
                            <use href="../App_Themes/CXone/icons/arrow-right.svg#arrow-right">
                        </use></svg>
                    </div>`,
                groupExpanded:
                    `<div class="sortIcons" id="icon-down" style="margin-bottom: 0; align-content: flex-end">
                        <svg class="icon-10" id="icon-down-svg">
                            <use href="../App_Themes/CXone/icons/arrow-down.svg#arrow-down"></use>
                        </svg>
                    </div>`
            },
            isExternalFilterPresent: () => true,
            doesExternalFilterPass: (node) => {
                const result = this.agGridHelperService.doesExternalFilterPassIncludeHidden(node, this.searchInput);
                return result;
            },
            getRowId: ({ data: rowData }) => rowData.ImportID,
            scrollbarWidth: 8
        };
    }

    async onGridReady(event: GridReadyEvent) {
        this.gridApi = event.api;
        this.gridApi.setGridOption('columnDefs', this.dataImportGridOptions.columnDefs);
        await this.loadDataImportList();
        this.gridApi.sizeColumnsToFit();
    }

    onSearchCriteriaChange(searchCriteria: string) {
        this.searchInput = searchCriteria;
        this.gridApi?.onFilterChanged();
        if (this.gridApi?.getDisplayedRowCount() === 0)
            this.gridApi.showNoRowsOverlay();
        else
            this.gridApi?.hideOverlay();
    };

    getTotalFeedsText(count: number): string {
        const key: string =
            count === 1 ? "Total_Feeds_Singular" : "Total_Feeds_Plural";
        const defaultValue: string = count === 1 ? "{0} import" : "{0} imports";
        return this.getLocalizationString(key, defaultValue);
    }

    getLocalizationString(key: string, defaultValue: string): string {
        const baseString = this.cvLocalizationService.getCachedText("CXone_Custom_Data_Import", key, defaultValue);
        return baseString;
    }

    public setRowCountLabel() {
        let count = 0;
        this.gridApi.forEachNodeAfterFilter((node) => {
            if (!node.group) {
                count++;
            }
        });
        this.dataImportLabel = this.cvLocalizationService.Format(this.getTotalFeedsText(count), count);
    }

    getPartialImportMessageText(count: number): string {
        const key: string =
            count === 1 ? "Partial_Import_Message_Singular" : "Partial_Import_Message_Plural";
        const defaultValue: string = count === 1 ? "Could not import {0} invalid record." : "Could not import {0} invalid records.";
        return this.getLocalizationString(key, defaultValue);
    }

    showImportDialog() {
        if (!this.modalRef) {
            sendMessageCXOne({hasNonSavedChanges: true});
            this.modalRef = this.dialogSrvc.open(
                CxoneCustomDataImportDialogComponent,
                {
                    width: "540px",
                    height: "346px",
                    data: {
                        rawDataDefinitions: this.rawDataDefinitions,
                    },
                }
            );
            this.modalRef.onClose.pipe(first()).subscribe((res) => {
                sendMessageCXOne({hasNonSavedChanges: false});
                this.modalRef = null;
                if (res.TotalPreprocessedRecordCount > 0 && res.TotalPreprocessedRecordCount !== res.FailedPreprocessedRecordsCount &&
                    res.FailedPreprocessedRecordsCount > 0) {
                    const alertTitle = this.getLocalizationString("Partial_Import_Title", "Imported Only Valid Records");
                    let alertMessage = this.cvLocalizationService.Format(this.getPartialImportMessageText(res?.FailedPreprocessedRecordsCount), res?.FailedPreprocessedRecordsCount);
                    this.showAlertMessage(alertTitle, alertMessage);
                } else if (res.TotalPreprocessedRecordCount > 0 && res.SuccessfulPreprocessedRecordsCount === 0) {
                    const alertTitile = this.getLocalizationString("Failed_Import_Title", "All Invalid Records");
                    const alertMessage = this.getLocalizationString("Failed_Import_Message", "Could not import records.");
                    this.showAlertMessage(alertTitile, alertMessage);
                } else {
                    this.toasterManager.showToaster("success",
                        this.getLocalizationString("Import_Successful", "Data import initiated successfully."));
                    this.loadDataImportList();
                }
            });
        }
    }

    showHideViewPage(bool: boolean) {
        this.showViewDetailsPage = bool;
    }

    async loadLocalization() {
        try {
            await this.cvLocalizationService.GetSection('CXone_Custom_Data_Import');
        }
        catch {
            console.log('Failed to load localization for CXone_Custom_Data_Import');
        }
        finally {
            this.searchPlaceholderText = this.getLocalizationString("Search_Data_Feeds", "Search data feeds");
        }
    }

    public async deleteListItemById(id: string) {
        this.showSpinner = true;
        try {
            await this.cxoneEtlDataImportService.deleteListItemById(id);
            this.toasterManager.showToaster('success', this.getLocalizationString("Data_Import_Deleted_Msg", "Data deleted successfully."));
        }
        catch (error) {
            this.toasterManager
                .showToaster('error',
                    this.getLocalizationString('Internal_Server_Error', 'Internal server error. Try again.'));
        }
        finally {
            this.loadDataImportList();
            this.showSpinner = false;
        }
    }

    public getFilterText(): string {
        return this.searchInput.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
    }

    public showAlertMessage(alertTitile: string, alertMessage: string) {
        const dialogData = {
            title: alertTitile,
            type: 'user-warning',
            message: `<div class="msg-line-1"> ${alertMessage} </div>`,
            closeButtonText: this.getLocalizationString("Okay", "Okay"),
        };
        const ref = this.dialogSrvc.open(MessageModalComponent, {
            width: '420px',
            height: '184px',
            styleClass: 'cx-one-ui modal-center testing',
            data: dialogData
        });

        ref.onClose.subscribe(x => {
            if (x) {
                this.loadDataImportList();
            }
        });
    }
}
